export type VehicleInputAction =
  | 'throttle-forward'
  | 'throttle-reverse'
  | 'steer-left'
  | 'steer-right'
  | 'pitch-up'
  | 'pitch-down'
  | 'jump'
  | 'boost'
  | 'handbrake'
  | 'air-roll-left'
  | 'air-roll-right';

export type CameraInputAction =
  | 'toggle-ball-camera'
  | 'camera-look-left'
  | 'camera-look-right'
  | 'camera-look-up'
  | 'camera-look-down'
  | 'rear-view';

export type GameInputAction =
  | VehicleInputAction
  | CameraInputAction
  | 'show-match-roster'
  | 'open-chat'
  | 'skip-replay'
  | 'open-session-menu'
  | 'spectate-next'
  | 'spectate-previous'
  | 'training-take-possession'
  | 'training-start-dribble'
  | 'training-pass-ball'
  | 'training-reset';

export type TrainingInputAction = Extract<GameInputAction, `training-${string}`>;

export const ALL_GAME_INPUT_ACTIONS: readonly GameInputAction[] = Object.freeze([
  'throttle-forward',
  'throttle-reverse',
  'steer-left',
  'steer-right',
  'pitch-up',
  'pitch-down',
  'jump',
  'boost',
  'handbrake',
  'air-roll-left',
  'air-roll-right',
  'toggle-ball-camera',
  'camera-look-left',
  'camera-look-right',
  'camera-look-up',
  'camera-look-down',
  'rear-view',
  'show-match-roster',
  'open-chat',
  'skip-replay',
  'open-session-menu',
  'spectate-next',
  'spectate-previous',
  'training-take-possession',
  'training-start-dribble',
  'training-pass-ball',
  'training-reset',
]);

const BASE_GAMEPLAY_ACTIONS: readonly GameInputAction[] = Object.freeze([
  'throttle-forward',
  'throttle-reverse',
  'steer-left',
  'steer-right',
  'pitch-up',
  'pitch-down',
  'jump',
  'boost',
  'handbrake',
  'air-roll-left',
  'air-roll-right',
  'toggle-ball-camera',
  'camera-look-left',
  'camera-look-right',
  'camera-look-up',
  'camera-look-down',
  'rear-view',
  'show-match-roster',
  'skip-replay',
  'open-session-menu',
]);

const TRAINING_ACTIONS: readonly TrainingInputAction[] = Object.freeze([
  'training-take-possession',
  'training-start-dribble',
  'training-pass-ball',
  'training-reset',
]);

const SPECTATOR_ACTIONS: readonly GameInputAction[] = Object.freeze([
  'spectate-next',
  'spectate-previous',
]);

const CONTINUOUS_ACTIONS = new Set<GameInputAction>([
  'throttle-forward',
  'throttle-reverse',
  'steer-left',
  'steer-right',
  'pitch-up',
  'pitch-down',
  'jump',
  'boost',
  'handbrake',
  'air-roll-left',
  'air-roll-right',
  'camera-look-left',
  'camera-look-right',
  'camera-look-up',
  'camera-look-down',
  'rear-view',
  'show-match-roster',
]);

export function gameplayInputActions(trainingEnabled: boolean, spectatorEnabled = false, onlineEnabled = false): ReadonlySet<GameInputAction> {
  return new Set([
    ...BASE_GAMEPLAY_ACTIONS,
    ...(trainingEnabled ? TRAINING_ACTIONS : []),
    ...(spectatorEnabled ? SPECTATOR_ACTIONS : []),
    ...(onlineEnabled ? ['open-chat'] as const : []),
  ]);
}

export function isTrainingInputAction(action: GameInputAction): action is TrainingInputAction {
  return action.startsWith('training-');
}

export function isContinuousInputAction(action: GameInputAction): boolean {
  return CONTINUOUS_ACTIONS.has(action);
}
